import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { format as formatDate } from 'date-fns';
import { CodeNameModel } from 'components/common/types';
import {
  ActiveColorFilter,
  ActiveColorFilterType,
  CampaignPlanError,
  CampaignPlanFilterOptions,
  CampaignPlanFilterRequest,
  CampaignPlanPoi,
} from 'components/common/types/CampaignPlan.types';
import { DirectSalesMediaType, DirectSalesMediaTypeLabel } from 'components/common/types/DirectSalesCampaign.types';
import { getCampaignPlanFilterOptions, getIsPOIsIncluded } from 'store/sharedCampaignPlan/selectors';
import { changeCampaignPlanDetails, changeCampaignPlanError } from 'store/sharedCampaignPlan/reducer';
import { getCampaignPlan, filterCampaignPlan } from 'modules/api/campaignPlan';
import { isCanceledError } from 'modules/api/request';
import { cancelFunctions } from 'utils/cancelFunctions';
import { FilterColorMap, useFilterColors } from './useFilterColors';

const buildChannelFilter = (
  selectedChannels: CodeNameModel[],
  channelColors: FilterColorMap,
): ActiveColorFilter | null => {
  if (selectedChannels.length === 0) return null;
  const colorMap = new Map<string, number[]>();
  selectedChannels.forEach((channel) => {
    const color = channelColors[channel.code];
    if (color) colorMap.set(channel.code, color.rgb);
  });

  return { type: 'channel', colorMap };
};

const buildFormatFilter = (
  selectedFormats: CodeNameModel[],
  formatColors: FilterColorMap,
): ActiveColorFilter | null => {
  if (selectedFormats.length === 0) return null;
  const colorMap = new Map<string, number[]>();
  selectedFormats.forEach((fmt) => {
    const color = formatColors[fmt.code];
    if (color) colorMap.set(fmt.code, color.rgb);
  });

  return { type: 'format', colorMap };
};

const buildMediaTypeFilter = (
  selectedMediaType: CodeNameModel | null,
  mediaTypeColors: FilterColorMap,
): ActiveColorFilter | null => {
  if (!selectedMediaType) return null;

  const color = mediaTypeColors[selectedMediaType.code];
  if (!color) return null;

  return { type: 'mediaType', colorMap: new Map([[selectedMediaType.code, color.rgb]]) };
};

const DEBOUNCE_MS = 300;
const CLUSTER_PROCESSING_DELAY = 200;
const EMPTY_POIS: CampaignPlanPoi[] = [];

const toSelectOptions = (values: string[]): CodeNameModel[] => values.map((value) => ({ code: value, name: value }));

const toMediaTypeOptions = (values: DirectSalesMediaType[]): CodeNameModel[] =>
  values.map((value) => ({ code: value, name: DirectSalesMediaTypeLabel[value] ?? value }));

const toPoiSelectOptions = (pois: CampaignPlanPoi[]): CodeNameModel[] =>
  pois.map((poi) => ({ code: poi.poiId, name: `${poi.displayName} (${poi.radius}m)` }));

interface BuildFilterRequestParams {
  selectedStartDate: Date | undefined;
  selectedEndDate: Date | undefined;
  selectedMediaType: CodeNameModel | null;
  selectedChannels: CodeNameModel[];
  selectedFormats: CodeNameModel[];
  selectedPois: CodeNameModel[];
}

const buildFilterRequest = ({
  selectedStartDate,
  selectedEndDate,
  selectedMediaType,
  selectedChannels,
  selectedFormats,
  selectedPois,
}: BuildFilterRequestParams): CampaignPlanFilterRequest | undefined => {
  const hasAnyFilter =
    (selectedStartDate && selectedEndDate) ||
    selectedMediaType ||
    selectedChannels.length > 0 ||
    selectedFormats.length > 0 ||
    selectedPois.length > 0;

  if (!hasAnyFilter) return undefined;

  const request: CampaignPlanFilterRequest = {};

  if (selectedStartDate && selectedEndDate) {
    request.range = {
      startDate: formatDate(selectedStartDate, 'yyyy-MM-dd'),
      endDate: formatDate(selectedEndDate, 'yyyy-MM-dd'),
    };
  }

  if (selectedMediaType) {
    request.mediaType = [selectedMediaType.code as DirectSalesMediaType];
  }

  if (selectedChannels.length > 0) {
    request.channel = selectedChannels.map((channel) => channel.code);
  }

  if (selectedFormats.length > 0) {
    request.format = selectedFormats.map((format) => format.code);
  }

  if (selectedPois.length > 0) {
    request.poi = selectedPois.map((poi) => poi.code);
  }

  return request;
};

export interface UseCampaignPlanFilters {
  filterOptions: CampaignPlanFilterOptions | null;
  isPOIsIncluded: boolean;
  selectedStartDate: Date | undefined;
  selectedEndDate: Date | undefined;
  selectedMediaType: CodeNameModel | null;
  selectedChannels: CodeNameModel[];
  selectedFormats: CodeNameModel[];
  selectedPois: CodeNameModel[];
  minDate: Date;
  maxDate: Date;
  mediaTypeOptions: CodeNameModel[];
  channelOptions: CodeNameModel[];
  formatOptions: CodeNameModel[];
  poiOptions: CodeNameModel[];
  hasActiveFilters: boolean;
  channelColors: FilterColorMap;
  formatColors: FilterColorMap;
  mediaTypeColors: FilterColorMap;
  activeColorFilter: ActiveColorFilter | null;
  selectedPoiData: CampaignPlanPoi[];
  setSelectedStartDate: (date: Date | undefined) => void;
  setSelectedEndDate: (date: Date | undefined) => void;
  handleMediaTypeSelect: (_name: string, value: CodeNameModel | CodeNameModel[] | null) => void;
  handleChannelSelect: (_name: string, value: CodeNameModel | CodeNameModel[] | null) => void;
  handleFormatSelect: (_name: string, value: CodeNameModel | CodeNameModel[] | null) => void;
  handlePoiSelect: (_name: string, value: CodeNameModel | CodeNameModel[] | null) => void;
  removeChannel: (code: string) => void;
  removeFormat: (code: string) => void;
  removePoi: (code: string) => void;
  clearAll: () => void;
}

export const useCampaignPlanFilters = (token?: string): UseCampaignPlanFilters => {
  const dispatch = useDispatch();
  const filterOptions = useSelector(getCampaignPlanFilterOptions);
  const isPOIsIncluded = useSelector(getIsPOIsIncluded);

  const [selectedStartDate, setSelectedStartDate] = useState<Date | undefined>();
  const [selectedEndDate, setSelectedEndDate] = useState<Date | undefined>();
  const [selectedMediaType, setSelectedMediaType] = useState<CodeNameModel | null>(null);
  const [selectedChannels, setSelectedChannels] = useState<CodeNameModel[]>([]);
  const [selectedFormats, setSelectedFormats] = useState<CodeNameModel[]>([]);
  const [selectedPois, setSelectedPois] = useState<CodeNameModel[]>([]);
  const [lastColorFilterType, setLastColorFilterType] = useState<ActiveColorFilterType | null>(null);

  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { channelColors, formatColors, mediaTypeColors } = useFilterColors(filterOptions);

  const minDate = useMemo(
    () => (filterOptions?.range ? new Date(filterOptions.range.startDate) : new Date()),
    [filterOptions?.range?.startDate],
  );
  const maxDate = useMemo(
    () => (filterOptions?.range ? new Date(filterOptions.range.endDate) : new Date()),
    [filterOptions?.range?.endDate],
  );

  const mediaTypeOptions = useMemo(
    () => toMediaTypeOptions(filterOptions?.mediaType ?? []),
    [filterOptions?.mediaType],
  );
  const channelOptions = useMemo(() => toSelectOptions(filterOptions?.channel ?? []), [filterOptions?.channel]);
  const formatOptions = useMemo(() => toSelectOptions(filterOptions?.format ?? []), [filterOptions?.format]);
  const poiOptions = useMemo(
    () => toPoiSelectOptions(filterOptions?.pois ?? []).sort((a, b) => a.name.localeCompare(b.name)),
    [filterOptions?.pois],
  );

  const allPois = filterOptions?.pois ?? EMPTY_POIS;

  const hasActiveFilters =
    (!!selectedStartDate && !!selectedEndDate) ||
    !!selectedMediaType ||
    selectedChannels.length > 0 ||
    selectedFormats.length > 0 ||
    selectedPois.length > 0;

  const selectedPoiData = useMemo(() => {
    if (!selectedPois.length || !allPois.length) return [];
    const selectedPoiIds = new Set(selectedPois.map((poi) => poi.code));

    return allPois.filter((poi) => selectedPoiIds.has(poi.poiId));
  }, [selectedPois, allPois]);

  const pendingColorFilter = useMemo((): ActiveColorFilter | null => {
    if (!lastColorFilterType) return null;

    if (lastColorFilterType === 'channel') {
      return (
        buildChannelFilter(selectedChannels, channelColors) ??
        buildFormatFilter(selectedFormats, formatColors) ??
        buildMediaTypeFilter(selectedMediaType, mediaTypeColors)
      );
    }

    if (lastColorFilterType === 'format') {
      return (
        buildFormatFilter(selectedFormats, formatColors) ??
        buildChannelFilter(selectedChannels, channelColors) ??
        buildMediaTypeFilter(selectedMediaType, mediaTypeColors)
      );
    }

    return (
      buildMediaTypeFilter(selectedMediaType, mediaTypeColors) ??
      buildChannelFilter(selectedChannels, channelColors) ??
      buildFormatFilter(selectedFormats, formatColors)
    );
  }, [
    lastColorFilterType,
    selectedChannels,
    selectedFormats,
    selectedMediaType,
    channelColors,
    formatColors,
    mediaTypeColors,
  ]);

  const pendingColorFilterRef = useRef<ActiveColorFilter | null>(null);
  pendingColorFilterRef.current = pendingColorFilter;

  const [activeColorFilter, setActiveColorFilter] = useState<ActiveColorFilter | null>(null);
  const colorSyncTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchFilteredPlan = useCallback(
    async (filters?: CampaignPlanFilterRequest) => {
      if (!token) return;

      try {
        const response = filters
          ? await filterCampaignPlan(token, cancelFunctions, filters)
          : await getCampaignPlan(token, cancelFunctions);

        dispatch(changeCampaignPlanDetails(response));
        if (colorSyncTimerRef.current) clearTimeout(colorSyncTimerRef.current);

        colorSyncTimerRef.current = setTimeout(() => {
          setActiveColorFilter(pendingColorFilterRef.current);
        }, CLUSTER_PROCESSING_DELAY);
      } catch (error) {
        if (isCanceledError(error)) return;
        setActiveColorFilter(null);
        dispatch(changeCampaignPlanError(error as CampaignPlanError));
      }
    },
    [token, dispatch],
  );

  useEffect(() => {
    return () => {
      if (colorSyncTimerRef.current) clearTimeout(colorSyncTimerRef.current);
      cancelFunctions.FILTER_CAMPAIGN_PLAN?.();
      cancelFunctions.FETCH_CAMPAIGN_PLAN?.();
    };
  }, []);

  const isInitialRender = useRef(true);

  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return undefined;
    }

    const filters = buildFilterRequest({
      selectedStartDate,
      selectedEndDate,
      selectedMediaType,
      selectedChannels,
      selectedFormats,
      selectedPois,
    });

    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

    debounceTimerRef.current = setTimeout(() => {
      fetchFilteredPlan(filters);
    }, DEBOUNCE_MS);

    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, [
    selectedStartDate,
    selectedEndDate,
    selectedMediaType,
    selectedChannels,
    selectedFormats,
    selectedPois,
    fetchFilteredPlan,
  ]);

  const handleMediaTypeSelect = useCallback((_name: string, value: CodeNameModel | CodeNameModel[] | null): void => {
    if (!value || (Array.isArray(value) && !value.length)) {
      setSelectedMediaType(null);
      return;
    }

    const newMediaType = Array.isArray(value) ? (value[0] ?? null) : value;
    setSelectedMediaType(newMediaType);
    if (newMediaType) setLastColorFilterType('mediaType');
  }, []);

  const handleChannelSelect = useCallback((_name: string, value: CodeNameModel | CodeNameModel[] | null): void => {
    if (!value) {
      setSelectedChannels([]);
      return;
    }

    const newChannels = Array.isArray(value) ? value : [value];
    setSelectedChannels(newChannels);
    if (newChannels.length > 0) setLastColorFilterType('channel');
  }, []);

  const handleFormatSelect = useCallback((_name: string, value: CodeNameModel | CodeNameModel[] | null): void => {
    if (!value) {
      setSelectedFormats([]);
      return;
    }

    const newFormats = Array.isArray(value) ? value : [value];
    setSelectedFormats(newFormats);
    if (newFormats.length > 0) setLastColorFilterType('format');
  }, []);

  const handlePoiSelect = useCallback((_name: string, value: CodeNameModel | CodeNameModel[] | null): void => {
    if (!value) {
      setSelectedPois([]);
      return;
    }

    const newPois = Array.isArray(value) ? value : [value];
    setSelectedPois(newPois);
  }, []);

  const removeChannel = useCallback((code: string): void => {
    setSelectedChannels((prev) => prev.filter((channel) => channel.code !== code));
  }, []);

  const removeFormat = useCallback((code: string): void => {
    setSelectedFormats((prev) => prev.filter((selectedFormat) => selectedFormat.code !== code));
  }, []);

  const removePoi = useCallback((code: string): void => {
    setSelectedPois((prev) => prev.filter((poi) => poi.code !== code));
  }, []);

  const clearAll = useCallback((): void => {
    setSelectedStartDate(undefined);
    setSelectedEndDate(undefined);
    setSelectedMediaType(null);
    setSelectedChannels([]);
    setSelectedFormats([]);
    setSelectedPois([]);
    setLastColorFilterType(null);
  }, []);

  return {
    filterOptions,
    isPOIsIncluded,
    selectedStartDate,
    selectedEndDate,
    selectedMediaType,
    selectedChannels,
    selectedFormats,
    selectedPois,
    minDate,
    maxDate,
    mediaTypeOptions,
    channelOptions,
    formatOptions,
    poiOptions,
    hasActiveFilters,
    channelColors,
    formatColors,
    mediaTypeColors,
    activeColorFilter,
    selectedPoiData,
    setSelectedStartDate,
    setSelectedEndDate,
    handleMediaTypeSelect,
    handleChannelSelect,
    handleFormatSelect,
    handlePoiSelect,
    removeChannel,
    removeFormat,
    removePoi,
    clearAll,
  };
};
